import { post, BadRequestError } from '../lib'

type UpgradeAuditNamedEntityRef = {
  id: number,
  name: string
}

type UpgradeAuditDeviceRef = UpgradeAuditNamedEntityRef & {
  serial: string
}

type UpgradeAuditVehicleRef = UpgradeAuditNamedEntityRef & {
  vin: string | null
}

type UpgradeAuditVersionValue = {
  ver: string,
  lastUpdated: string
}

type UpgradeAuditDownloadRef = {
  id: number,
  dateCreated: string,
  received: string | null
}

type UpgradeAuditDownloadFileRef = {
  id: number,
  name: string
}

type UpgradeAuditScriptIdRef = {
  id: number
}

type UpgradeAuditScriptRef = UpgradeAuditNamedEntityRef | null

type UpgradeAuditPendingScriptFileSendJob = {
  type: 'SCRIPT',
  id: number,
  runStatus: 'NEW' | 'STARTED',
  download: UpgradeAuditDownloadRef,
  downloadFile: UpgradeAuditDownloadFileRef,
  script: UpgradeAuditScriptIdRef
}

type UpgradeAuditPendingSoftwareUpdateFileSendJob = {
  type: 'SOFTWARE_UPDATE',
  id: number,
  runStatus: 'NEW' | 'STARTED',
  download: UpgradeAuditDownloadRef,
  downloadFile: UpgradeAuditDownloadFileRef
}

export type DeviceUpdateAuditItem = {
  device: UpgradeAuditDeviceRef,
  vehicle: UpgradeAuditVehicleRef,
  fleet: UpgradeAuditNamedEntityRef,
  organization: UpgradeAuditNamedEntityRef,
  versions: {
    os: UpgradeAuditVersionValue | null,
    client: UpgradeAuditVersionValue | null,
    zchip: UpgradeAuditVersionValue | null,
    schip: UpgradeAuditVersionValue | null
  },
  script: {
    current: UpgradeAuditScriptRef,
    lastSent: {
      download: UpgradeAuditDownloadRef,
      downloadFile: UpgradeAuditDownloadFileRef,
      script: UpgradeAuditScriptIdRef,
      matchesCurrent: boolean
    } | null
  },
  softwareUpdate: {
    lastSent: {
      download: UpgradeAuditDownloadRef,
      downloadFile: UpgradeAuditDownloadFileRef
    } | null
  },
  pendingFileSendJobs: Array<UpgradeAuditPendingScriptFileSendJob | UpgradeAuditPendingSoftwareUpdateFileSendJob>
}

type DeviceUpdatesRequest = {
  organizationIds: number[],
  fleetIds: number[]
}

export type GetDeviceUpdateAuditParams = {
  organizations: number[],
  fleets: number[]
}

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null
)

const isDeviceUpdateAuditItem = (value: unknown): value is DeviceUpdateAuditItem => {
  if (!isRecord(value)) {
    return false
  }

  return isRecord(value.device)
    && isRecord(value.vehicle)
    && isRecord(value.fleet)
    && isRecord(value.organization)
    && isRecord(value.versions)
    && isRecord(value.script)
    && isRecord(value.softwareUpdate)
    && Array.isArray(value.pendingFileSendJobs)
}

export const formatDeviceUpdateAuditResponse = (res: unknown): DeviceUpdateAuditItem[] => {
  if (!Array.isArray(res) || !res.every(isDeviceUpdateAuditItem)) {
    throw new Error('Invalid device update audit response')
  }

  return res
}

export const getDeviceUpdateAudit = async ({organizations, fleets}: GetDeviceUpdateAuditParams): Promise<DeviceUpdateAuditItem[]> => {
  try {
    if (!(organizations?.length || fleets?.length)) {
      throw new BadRequestError([
        {'field': 'organizations', 'message': 'At least one of organizations or fleets must be provided'},
        {'field': 'fleets', 'message': 'At least one of organizations or fleets must be provided'}
      ], 'At least one of organizations or fleets must be provided')
    }

    const res = await post<DeviceUpdatesRequest, unknown>({
      path: '/api/v1/devices/updatesAudit',
      body: {
        organizationIds: organizations,
        fleetIds: fleets
      }
    })

    return formatDeviceUpdateAuditResponse(res)
  } catch (e) {
    console.error('Error fetching device updates', e)
    throw e
  }
}
